import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAdList, deleteAdUnit } from '../adActions';
import { useIsMounted } from '../../../shared/hooks';
import { APP_ROUTES, AD_STATUS_STYLES, AD_SIZES, UI_MESSAGES } from '../../../shared/constants';
import { formatCurrency, formatDate } from '../../../shared/utils/helpers';
import { extractContractError, formatErrorForDisplay } from  '@bafkal/ui-shared/utils';
import { Table, Card, Button, LoadingSpinner, ErrorMessage, PageHeader } from '@bafkal/ui-shared/components';
import { colors } from '@bafkal/ui-shared/tokens';

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${AD_STATUS_STYLES[status] || AD_STATUS_STYLES.DRAFT}`}>
    {status}
  </span>
);

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

const AdList = () => {
  const dispatch = useDispatch();
  const adState = useSelector((state) => state.ads);
  const { loading, error, ads, total, page, pageSize } = adState;
  const [deleteLoading, setDeleteLoading] = useState(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    dispatch(fetchAdList(page)).catch(() => {});
  }, [dispatch, page]);

  const handlePageChange = (newPage) => {
    dispatch(fetchAdList(newPage));
  };

  const handleDelete = async (adId, adName) => {
    if (!window.confirm(UI_MESSAGES.AD.DELETE_CONFIRM)) {
      return;
    }

    setDeleteLoading(adId);
    try {
      await dispatch(deleteAdUnit(adId));
      await dispatch(fetchAdList(page));
    } catch (err) {
      const contractError = extractContractError(err, UI_MESSAGES.AD.DELETE_FAILED);
      alert(formatErrorForDisplay(contractError));
    } finally {
      if (isMounted.current) setDeleteLoading(null);
    }
  };

  const loadData = () => {
    dispatch(fetchAdList(page));
  };

  if (loading && ads.length === 0) {
    return (
      <div className="p-4">
        <LoadingSpinner size="xl" className="mt-20" />
      </div>
    );
  }

  // Column definitions for the shared Table component
  const columns = [
    {
      key: 'name',
      header: 'Ad Name',
      render: (_, ad) => (
        <div>
          <div style={{ fontWeight: 500 }}>{ad.name}</div>
          <div style={{ fontSize: '0.75rem', color: colors.neutral[500] }}>ID: {ad.id}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, ad) => <StatusBadge status={ad.status} />,
    },
    {
      key: 'size',
      header: 'Size',
      render: (_, ad) => AD_SIZES[ad.size]?.label || ad.size || '—',
    },
    {
      key: 'type',
      header: 'Type',
      render: (_, ad) => <span style={{ textTransform: 'capitalize' }}>{ad.type || '—'}</span>,
    },
    {
      key: 'floorPrice',
      header: 'Floor Price',
      render: (_, ad) => (ad.floorPrice != null ? formatCurrency(ad.floorPrice) : '—'),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (_, ad) => formatDate(ad.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (_, ad) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Link to={APP_ROUTES.INVENTORY.EDIT(ad.id)} style={{ color: colors.primary[600], fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none' }}>
            Edit
          </Link>
          <Button
            variant="danger"
            size="sm"
            disabled={deleteLoading === ad.id}
            onClick={() => handleDelete(ad.id, ad.name)}
          >
            {deleteLoading === ad.id ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Ad Placements"
        subtitle="A list of all ad units in your account including their name, status, and performance metrics."
        action={
          <Link
            to={APP_ROUTES.INVENTORY.NEW}
            className="bafkal-btn bafkal-btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            Add new ad
          </Link>
        }
      />

      {error ? (
        <ErrorMessage message={error.message || UI_MESSAGES.GENERIC.ERROR} onRetry={loadData} />
      ) : ads.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <svg style={{ margin: '0 auto 16px', width: '48px', height: '48px', color: colors.neutral[400] }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 style={{ fontWeight: 500, marginBottom: '4px' }}>No ads</h3>
            <p style={{ fontSize: '0.875rem', color: colors.neutral[500], marginBottom: '16px' }}>Get started by creating a new ad unit.</p>
            <Link
              to={APP_ROUTES.INVENTORY.NEW}
              className="bafkal-btn bafkal-btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}
            >
              Create your first ad
            </Link>
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="sm:hidden space-y-4 mb-4">
            {ads.map((ad) => (
              <Card key={ad.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{ad.name}</div>
                    <div style={{ fontSize: '0.75rem', color: colors.neutral[500] }}>ID: {ad.id}</div>
                  </div>
                  <StatusBadge status={ad.status} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.875rem', marginBottom: '12px' }}>
                  <div><div style={{ color: colors.neutral[500], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</div><div style={{ fontWeight: 500 }}>{AD_SIZES[ad.size]?.label || ad.size || '—'}</div></div>
                  <div><div style={{ color: colors.neutral[500], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</div><div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{ad.type || '—'}</div></div>
                  <div><div style={{ color: colors.neutral[500], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Floor Price</div><div style={{ fontWeight: 500 }}>{ad.floorPrice != null ? formatCurrency(ad.floorPrice) : '—'}</div></div>
                  <div><div style={{ color: colors.neutral[500], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</div><div style={{ fontWeight: 500 }}>{formatDate(ad.createdAt)}</div></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: `1px solid ${colors.neutral[100]}`, paddingTop: '12px' }}>
                  <Link to={APP_ROUTES.INVENTORY.EDIT(ad.id)} style={{ color: colors.primary[600], fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none' }}>Edit</Link>
                  <Button variant="danger" size="sm" disabled={deleteLoading === ad.id} onClick={() => handleDelete(ad.id, ad.name)}>
                    {deleteLoading === ad.id ? 'Deleting…' : 'Delete'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop table view */}
          <div className="hidden sm:block">
            <Table columns={columns} data={ads} keyField="id" emptyMessage="No ad units found." />
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bafkal-bg-surface)', border: `1px solid ${colors.neutral[200]}`, borderRadius: '8px', padding: '12px 16px' }}>
              <div style={{ fontSize: '0.875rem', color: colors.neutral[700] }}>
                Showing <strong>{(page - 1) * pageSize + 1}</strong>–<strong>{Math.min(page * pageSize, total)}</strong> of <strong>{total}</strong> ad units
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => handlePageChange(page - 1)}>Previous</Button>
                <Button variant="secondary" disabled={page * pageSize >= total || loading} onClick={() => handlePageChange(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdList;